import { BQ_PROJECT_NAME } from 'config';
import { scaleWeights, scaleAllWeights } from 'utils/helpers';
import { BOUNDARY_DATASETS } from './config/boundaryConfig';

const CRITICALITY_SCALED_BQ_TABLE = `\`${BQ_PROJECT_NAME}.indexing_criticality.h3_r10_scaled\``;
const H3_SCALED_BQ_TABLE = `\`${BQ_PROJECT_NAME}.indexing_us.h3_scaled_r9\``;
// H3 is aperture-7: each r8 cell has exactly 7 r9 children. The vulnerability
// index moved from r8 to r9, so an r8 boundary now covers 7x as many cells.
const R8_TO_R9_CHILDREN = 7;
const H3_EROSION_BQ_TABLE = `\`${BQ_PROJECT_NAME}.app.erosion_h3\``;

export function getBqTable(sourceName) {
  // get the BQ tablename of this source
  let bqTable;
  BOUNDARY_DATASETS.forEach((config) => {
    if (config.sourceId === sourceName) {
      bqTable = `\`${BQ_PROJECT_NAME}.${config.table}\``;
    }
  });
  return bqTable;
}

// "Average criticality" = mean weighted score over the HPMS (corridor) cells
// that fall inside the boundary. The criticality table only contains hpms=TRUE
// cells, so the divisor `count(*)` counts exactly those cells — NOT every cell
// intersecting the boundary. This differs from queryAverageH3Index below, which
// divides by `intersectingH3.length` (all boundary cells). The two denominators
// are intentionally different: criticality is a corridor metric (r9, HPMS-only),
// vulnerability is an area metric (r8, full boundary). Do not "align" them.
export function queryAverageCriticalityIndex(
  weights,
  selectedUuid,
  selectedBoundarySource
) {
  const bqTable = getBqTable(selectedBoundarySource);
  const scaledWeights = scaleWeights(weights);

  const selectList = [];
  for (const [k, v] of Object.entries(scaledWeights)) {
    selectList.push(`sum(${k} * ${v})`);
  }

  // Filter r9 cells by their r8 parent so the boundary's intersectingH3 arrays
  // (stored at r8) keep working without recomputing boundaries.
  const query = `
        select (${selectList.join(' + ')}) / count(*) as avg_score
        from ${CRITICALITY_SCALED_BQ_TABLE}
        where \`carto-un\`.carto.H3_TOPARENT(h3, 8) in (
            with d as (
                select intersectingH3 from ${bqTable} where uuid = '${selectedUuid}'
            )
            select x from d, unnest(intersectingH3) as x
        )
    `;

  return query;
}

export function queryAverageH3Index(
  topicWeights,
  datasetWeights,
  selectedUuid,
  selectedBoundarySource,
  intersectingH3
) {
  const bqTable = getBqTable(selectedBoundarySource);

  const scaledWeights = scaleAllWeights(topicWeights, datasetWeights);

  // Vulnerability divides by the count of all cells intersecting the boundary
  // (area metric). This is deliberately different from queryAverageCriticalityIndex,
  // which divides by SQL count(*) over HPMS-only cells (corridor metric).
  // intersectingH3 is stored at r8, but the index table is now r9: every r8 cell
  // has exactly 7 r9 children, so the r9 cell count is intersectingH3.length * 7.
  // Guard against an empty boundary: dividing by 0 would emit Infinity/NaN in the SQL.
  const divisor = intersectingH3.length * R8_TO_R9_CHILDREN || 1;
  const selectList = [];
  for (const [k, v] of Object.entries(scaledWeights)) {
    const select = `(sum(${k} * ${v}) / ${divisor})`;
    selectList.push(select);
  }

  // Filter r9 cells by their r8 parent so the boundary's intersectingH3 arrays
  // (stored at r8) keep working without recomputing boundaries.
  const query = `
          select ${selectList.join(' + ')} as avg_score
          from ${H3_SCALED_BQ_TABLE}
          where \`carto-un\`.carto.H3_TOPARENT(h3, 8) in (
            with d as (
                select intersectingH3 from ${bqTable} where uuid = '${selectedUuid}'
            )
            select x from d, unnest(intersectingH3) as x
        )
      `;

  return query;
}


export function queryAverageErosionIndex(
  selectedUuid,
  selectedBoundarySource
) {
  const bqTable = getBqTable(selectedBoundarySource);
  const query = `
  select AVG(rusle) as avg_score
  from ${H3_EROSION_BQ_TABLE}
  where h3 in (
    with d as (
        select intersectingH3 from ${bqTable} where uuid = '${selectedUuid}'
    )
    select x from d, unnest(intersectingH3) as x
)
`;

return query;
}